import { ControlledInput, ControlledSelect } from '@/components/controlled';
import { cn } from '@/lib/utils';
import { AccountingUserTypeEnum, AccountingUserStatusEnum } from '@/enums';
import { useFormContext, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { getSelectOptions, nameof } from '@/functions';
import dayjs from 'dayjs';
import { LabelRequired } from '@/components/hoc';
import { TFormData } from '.';
import { User, Activity, Coins } from 'lucide-react';

const FormCreateAccountingUserBody = ({ isEditMode = false }: { isEditMode?: boolean }) => {

    const { control, setValue } = useFormContext<TFormData>();
    const readonly = useWatch({ control, name: 'readonly' });
    const isSubmitting = useWatch({ control, name: 'isSubmitting' });
    const userType = useWatch({ control, name: 'type' });
    const status = useWatch({ control, name: 'status' });
    const disabledAt = useWatch({ control, name: 'disabledAt' });
    const startedAt = useWatch({ control, name: 'startedAt' });

    const isDisabled = readonly || isSubmitting;
    const isEmployee = userType === AccountingUserTypeEnum.EMPLOYEE;
    const isInvestor = userType === AccountingUserTypeEnum.INVESTOR;
    const isStatusDisabled = status === AccountingUserStatusEnum.DISABLED;

    useEffect(() => {
        // Default startedAt only on CREATION (not edit mode) and if not present
        if (isEmployee && !startedAt && !isEditMode) {
            setValue('startedAt', dayjs().format('YYYY-MM-DD'));
        }

        // Default disabledAt if status just changed to DISABLED and no date set
        if (isEmployee && isStatusDisabled && !disabledAt) {
            setValue('disabledAt', dayjs().format('YYYY-MM-DD'));
        }
    }, [isEmployee, isStatusDisabled, disabledAt, startedAt, isEditMode, setValue]);

    const SectionHeader = ({ icon: Icon, title, className = "" }: { icon: any, title: string, className?: string }) => (
        <div className={`hidden md:flex items-center space-x-2 pb-2 border-b border-border/40 mb-4 ${className}`}>
            <div className="p-1.5 bg-primary/10 rounded-md text-primary">
                <Icon size={16} />
            </div>
            <h3 className="text-sm font-semibold tracking-wide uppercase text-foreground/80">{title}</h3>
        </div>
    );

    return (
        <div className='py-2 md:py-4 space-y-4 md:space-y-8 animate-in fade-in duration-500'>

            {/* Informations Personnelles */}
            <section>
                <SectionHeader icon={User} title="Informations Générales" />
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6'>
                    <div className='flex flex-col gap-1.5'>
                        <LabelRequired className="text-[11px] md:text-xs">Nom complet</LabelRequired>
                        <ControlledInput
                            disabled={isDisabled}
                            control={control}
                            rules={{ required: 'Le nom est requis' }}
                            name={nameof<TFormData>('name')}
                            placeholder={'Ex: Jean Dupont'}
                            className="bg-muted/30 focus:bg-background transition-colors h-9 md:h-10"
                        />
                    </div>
                    <div className='flex flex-col gap-1.5'>
                        <LabelRequired className="text-[11px] md:text-xs">Rôle</LabelRequired>
                        <ControlledInput
                            disabled={isDisabled}
                            control={control}
                            rules={{ required: 'Le rôle est requis' }}
                            name={nameof<TFormData>('role')}
                            placeholder={'Ex: Développeur Senior'}
                            className="bg-muted/30 focus:bg-background transition-colors h-9 md:h-10"
                        />
                    </div>
                </div>
            </section>

            {/* Configuration */}
            <section>
                <SectionHeader icon={Activity} title="Configuration du Compte" />
                <div className='grid grid-cols-2 gap-3 md:gap-6'>
                    <div className='flex flex-col gap-1.5'>
                        <LabelRequired className="text-[11px] md:text-xs">Type</LabelRequired>
                        <ControlledSelect
                            disabled={isDisabled}
                            control={control}
                            rules={{ required: 'Le type est requis' }}
                            name={nameof<TFormData>('type')}
                            placeholder='Type'
                            options={getSelectOptions('AccountingUserTypeEnum')}
                        />
                    </div>
                    <div className='flex flex-col gap-1.5'>
                        <LabelRequired className="text-[11px] md:text-xs">Statut</LabelRequired>
                        <ControlledSelect
                            disabled={isDisabled}
                            control={control}
                            rules={{ required: 'Le statut est requis' }}
                            name={nameof<TFormData>('status')}
                            placeholder='Statut'
                            options={getSelectOptions('AccountingUserStatusEnum')}
                        />
                    </div>
                </div>
            </section>

            {/* Conditionnel: Rémunération / Participation */}
            {(isEmployee || isInvestor) && (
                <section className="bg-muted/20 p-3 md:p-4 rounded-lg border border-border/40">
                    <div className='flex items-center space-x-2 mb-3 md:mb-4'>
                        <Coins size={14} className="text-primary" />
                        <h3 className="text-[12px] md:text-sm font-medium text-foreground">
                            {isEmployee ? 'Rémunération' : 'Participation'}
                        </h3>
                    </div>

                    {isEmployee && (
                        <div className='flex flex-col gap-3'>
                            <div className='flex flex-col gap-1.5'>
                                <LabelRequired className="text-[11px] md:text-xs">Salaire (EUR) <span className="hidden md:inline-block text-xs font-normal text-muted-foreground ml-1">/ mois</span></LabelRequired>
                                <ControlledInput
                                    disabled={isDisabled}
                                    control={control}
                                    type='number'
                                    rules={{
                                        required: 'Le salaire est requis',
                                        min: { value: 1, message: 'Supérieur à 0' }
                                    }}
                                    name={nameof<TFormData>('baseSalaryEur')}
                                    placeholder={'4500'}
                                    className="font-mono text-base md:text-lg h-9 md:h-10"
                                />
                            </div>

                            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                                <div className={cn(
                                    'flex flex-col gap-1.5',
                                    !isStatusDisabled && 'md:col-span-2'
                                )}>
                                    <LabelRequired className="text-[11px] md:text-xs">Date de début</LabelRequired>
                                    <ControlledInput
                                        disabled={isDisabled}
                                        control={control}
                                        type='date'
                                        rules={{ required: 'La date de début est requise' }}
                                        name={nameof<TFormData>('startedAt')}
                                        className="bg-muted/30 focus:bg-background transition-colors h-9 md:h-10"
                                    />
                                </div>

                                {isStatusDisabled && (
                                    <div className='flex flex-col gap-1.5 animate-in slide-in-from-top-1 duration-300'>
                                        <LabelRequired className="text-[11px] md:text-xs">Date de fin</LabelRequired>
                                        <ControlledInput
                                            disabled={isDisabled}
                                            control={control}
                                            type='date'
                                            rules={{
                                                required: 'La date de fin est requise',
                                                validate: value => {
                                                    if (!startedAt) return true;
                                                    return dayjs(value).isAfter(dayjs(startedAt)) || 'La date de fin doit être supérieure à la date de début';
                                                }
                                            }}
                                            name={nameof<TFormData>('disabledAt')}
                                            className="bg-muted/30 focus:bg-background transition-colors h-9 md:h-10"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {isInvestor && (
                        <div className='flex flex-col gap-1.5'>
                            <LabelRequired className="text-[11px] md:text-xs">Pourcentage (%)</LabelRequired>
                            <ControlledInput
                                disabled={isDisabled}
                                control={control}
                                type='number'
                                step='0.01'
                                rules={{
                                    required: 'Requis',
                                    min: { value: 0.01, message: 'Min 0.01' },
                                    max: { value: 100, message: 'Max 100' }
                                }}
                                name={nameof<TFormData>('percentage')}
                                placeholder={'15.5'}
                                className="font-mono text-base md:text-lg h-9 md:h-10"
                            />
                        </div>
                    )}
                </section>
            )}

        </div>
    );
};

export default FormCreateAccountingUserBody;
